import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { 
  CheckCircle, Target, FileText, ArrowLeft, Shield, Star, Sparkles, 
  Eye, Download, MapPin, Mail, Phone, Calendar, Clock, TrendingDown, 
  AlertCircle, TrendingUp, Trophy, Gift, Info, Lightbulb, AlertTriangle,
  Search, Zap, Award, Users, XCircle, CheckCircle2, Upload, 
  ClipboardPaste, Plus, X, Globe, Paperclip, Building2, Link as LinkIcon,
  Loader2, Rocket
} from 'lucide-react';

type SourceType = 'jobintel' | 'company-insights' | 'upload' | 'paste' | 'generate-new' | '';

interface JobIntelReport {
  id: string;
  company_name: string | null;
  job_title: string | null;
  hiring_manager: any;
  pain_points: any;
  company_goals: any;
  separation_strategy: any;
  job_url: string | null;
  created_at: string;
}

interface CompanyInsightsReport {
  id: string;
  company_name: string | null;
  job_title: string | null;
  job_url: string | null;
  created_at: string;
  insights: any;
}

export default function SolutionAsset() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showWhyDialog, setShowWhyDialog] = useState(false);
  const [showWhatDialog, setShowWhatDialog] = useState(false);

  // Form states
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sourceType, setSourceType] = useState<SourceType>('');
  
  // Reports
  const [jobIntelReports, setJobIntelReports] = useState<JobIntelReport[]>([]);
  const [companyInsightsReports, setCompanyInsightsReports] = useState<CompanyInsightsReport[]>([]);
  const [selectedJobIntelId, setSelectedJobIntelId] = useState<string | null>(null);
  const [selectedCompanyInsightId, setSelectedCompanyInsightId] = useState<string | null>(null);
  
  // Upload/Paste states
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pastedContent, setPastedContent] = useState('');
  
  // Generate new states
  const [newJobUrl, setNewJobUrl] = useState('');
  const [newJobDescription, setNewJobDescription] = useState('');
  const [isGeneratingJobIntel, setIsGeneratingJobIntel] = useState(false);
  
  // Resume selection states (Step 2)
  const [resumeSource, setResumeSource] = useState<'library' | 'upload' | 'paste' | ''>('');
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  const [selectedResumeText, setSelectedResumeText] = useState<string>('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [pastedResumeText, setPastedResumeText] = useState('');
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [processingResume, setProcessingResume] = useState(false);
  
  // Step 3: Additional Context states
  const [additionalFiles, setAdditionalFiles] = useState<Array<{file: File, name: string, processing: boolean, text?: string}>>([]);
  const [additionalPastedContent, setAdditionalPastedContent] = useState('');
  const [researchUrls, setResearchUrls] = useState<Array<{url: string, status: 'pending' | 'loading' | 'success' | 'error', content?: string}>>([]);
  const [newResearchUrl, setNewResearchUrl] = useState('');

  // Dummy solution asset data for sample report
  const solutionAssetData = {
    candidate: {
      name: "Jordan Avery, PharmD",
      targetRole: "Pharmacist – CVS Health",
      location: "[City, State]",
      email: "jordan.avery@email.com",
      phone: "(555) 123-4567"
    },
    headline: "Reduce prescription wait times by 10 minutes, strengthen compliance safeguards, and improve patient satisfaction by 15% within 90 days.",
    context: "Retail pharmacies are under intense pressure: rising prescription volumes, stricter controlled-substance compliance, and increasing customer dissatisfaction with long wait times.",
    painPoints: [
      { title: "Long Prescription Wait Times", description: "Customers waiting 20–30 minutes leads to frustration and lost loyalty." },
      { title: "Compliance Risks", description: "High regulatory scrutiny around controlled substances demands airtight daily processes." },
      { title: "Fragmented Service Experience", description: "Customers see the pharmacy counter as disconnected from the broader CVS wellness model." }
    ],
    solutions: [
      { title: "Streamline Prescription Workflow", action: "Implement tech-enabled queue tracking.", impact: "Reduce wait time from 25 min → 15 min.", proof: "Reduced fill turnaround 18% at Walgreens." },
      { title: "Daily Compliance Double-Check", action: "Two-layer verification for controlled substances.", impact: "Minimize compliance incidents.", proof: "Zero violations in 2 years." },
      { title: "Integrate Pharmacist Consults", action: "Connect consults to front-of-store wellness.", impact: "Better health outcomes + revenue.", proof: "Boosted satisfaction scores 12%." }
    ],
    quickWinPlan: [
      { period: "First 30 Days", actions: "Workflow time study; queue visibility; tech hand-off training." },
      { period: "60 Days", actions: "Launch daily compliance double-check log." },
      { period: "90 Days", actions: "Integrate consults with OTC + nutrition categories." }
    ],
    kpis: [
      { metric: "Prescription fill time", target: "↓ 40%" },
      { metric: "Compliance incidents", target: "↓ 20%" },
      { metric: "Customer satisfaction", target: "+15%" },
      { metric: "Consult uptake", target: "+10%" }
    ],
    pastResults: [
      "Reduced fill times 18% in Walgreens district pilot.",
      "Zero compliance incidents in 2 years.",
      "Boosted consult engagement 22%."
    ],
    risks: [
      { risk: "Staff resistance to new workflow.", mitigation: "15-minute daily huddles and job aids." },
      { risk: "Customer adoption slower than expected.", mitigation: "ExtraCare Rewards incentives." }
    ]
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load available JobIntel reports
  useEffect(() => {
    const fetchReports = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        const { data: jobIntelData, error: jobIntelError } = await supabase
          .from('job_intel_reports')
          .select('id, company_name, job_title, hiring_manager, pain_points, company_goals, separation_strategy, job_url, created_at')
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (jobIntelError) throw jobIntelError;
        setJobIntelReports(jobIntelData || []);

        const { data: companyData, error: companyError } = await supabase
          .from('company_insights_reports')
          .select('id, company_name, job_title, job_url, created_at, insights')
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (companyError) throw companyError;
        setCompanyInsightsReports(companyData || []);
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReports();
  }, [user]);

  // Load user's resumes for library selection
  useEffect(() => {
    const fetchResumes = async () => {
      if (!user || resumeSource !== 'library') return;
      
      setLoadingResumes(true);
      try {
        const { data, error } = await supabase
          .from('resumes')
          .select('id, file_name, title, content, original_file_url')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (!error && data) {
          setUserResumes(data.map(r => ({
            ...r,
            resume_name: r.title,
            resume_text: r.content
          })));
        }
      } catch (error) {
        console.error('Error fetching resumes:', error);
      } finally {
        setLoadingResumes(false);
      }
    };
    
    fetchResumes();
  }, [user, resumeSource]);

  const isResumeValid = () => {
    if (!resumeSource) return false;
    switch (resumeSource) {
      case 'library': return !!selectedResumeId && !!selectedResumeText;
      case 'upload': return !!resumeFile && !!selectedResumeText;
      case 'paste': return pastedResumeText.trim().length > 50;
      default: return false;
    }
  };

  const isSourceValid = () => {
    if (!sourceType) return false;
    switch (sourceType) {
      case 'jobintel': return !!selectedJobIntelId;
      case 'company-insights': return !!selectedCompanyInsightId;
      case 'upload': return !!uploadedFile;
      case 'paste': return pastedContent.trim().length > 50;
      case 'generate-new': return newJobDescription.trim().length > 50 || newJobUrl.trim().length > 10;
      default: return false;
    }
  };

  const handleGenerate = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!isSourceValid()) {
      toast({ title: "Source Required", description: "Please provide intelligence source.", variant: "destructive" });
      return;
    }

    if (!isResumeValid()) {
      toast({ title: "Resume Required", description: "Please select, upload, or paste your resume.", variant: "destructive" });
      return;
    }

    setIsGenerating(true);
    
    try {
      let jobApplicationIntel: any = {};
      let sourceReportId: string | null = null;

      if (sourceType === 'jobintel' && selectedJobIntelId) {
        const selectedReport = jobIntelReports.find(r => r.id === selectedJobIntelId);
        if (!selectedReport) throw new Error('Selected JobIntel report not found');
        
        jobApplicationIntel = {
          company_name: selectedReport.company_name || '',
          job_title: selectedReport.job_title || '',
          hiring_manager: selectedReport.hiring_manager || {},
          pain_points: selectedReport.pain_points || [],
          strategic_goals: selectedReport.company_goals || [],
          separation_strategy: selectedReport.separation_strategy || ''
        };
        sourceReportId = selectedJobIntelId;
      } else if (sourceType === 'company-insights' && selectedCompanyInsightId) {
        const selectedReport = companyInsightsReports.find(r => r.id === selectedCompanyInsightId);
        if (!selectedReport) throw new Error('Selected Company Insights report not found');
        
        jobApplicationIntel = {
          company_name: selectedReport.company_name || '',
          job_title: selectedReport.job_title || '',
          company_insights: selectedReport.insights || {}
        };
        sourceReportId = selectedCompanyInsightId;
      } else if (sourceType === 'paste') {
        jobApplicationIntel = { raw_content: pastedContent, source_type: 'pasted_content' };
      } else if (sourceType === 'upload' && uploadedFile) {
        const fileExt = uploadedFile.name.split('.').pop();
        const filePath = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('uploaded-documents')
          .upload(filePath, uploadedFile);
        
        if (uploadError) throw uploadError;
        
        const { data: urlData } = supabase.storage
          .from('uploaded-documents')
          .getPublicUrl(filePath);
        
        jobApplicationIntel = { document_url: urlData.publicUrl, source_type: 'uploaded_document' };
      } else if (sourceType === 'generate-new') {
        setIsGeneratingJobIntel(true);
        
        const { error: triggerError } = await supabase.functions.invoke('trigger-job-intel', {
          body: { user_id: user.id, job_url: newJobUrl || undefined, job_description: newJobDescription }
        });
        
        if (triggerError) throw triggerError;
        
        toast({ title: "JobIntel Report Queued", description: "Once complete, you can use it for your Solution Asset." });
        setIsGeneratingJobIntel(false);
        navigate('/job-intel-reports');
        return;
      }

      const resumeText = resumeSource === 'paste' ? pastedResumeText : selectedResumeText;

      const additionalContext = {
        files: additionalFiles.filter(f => f.text).map(f => ({ name: f.name, content: f.text })),
        pasted_content: additionalPastedContent || null,
        research_urls: researchUrls.filter(u => u.status === 'success').map(u => ({ url: u.url, content: u.content })),
      };

      const { error } = await supabase.functions.invoke('trigger-solution-asset', {
        body: {
          user_id: user.id,
          job_application_intel: jobApplicationIntel,
          resume_text: resumeText,
          additional_context: additionalContext,
          job_intel_report_id: sourceReportId,
          source_type: sourceType
        }
      });

      if (error) throw error;

      toast({ title: "Generation Started!", description: "Your Solution Asset is being created. Check your reports page." });
      navigate('/solution-asset-reports');
      
    } catch (error: any) {
      console.error('Error:', error);
      toast({ title: "Generation Failed", description: error.message || "Failed to start generation.", variant: "destructive" });
    } finally {
      setIsGenerating(false);
      setIsGeneratingJobIntel(false);
    }
  };

  const selectedReport = sourceType === 'jobintel' 
    ? jobIntelReports.find(r => r.id === selectedJobIntelId)
    : sourceType === 'company-insights'
    ? companyInsightsReports.find(r => r.id === selectedCompanyInsightId)
    : null;

  const whatYouReceiveItems = [
    { title: "The Hook", description: "A powerful 2-3 sentence opening that proves insider knowledge" },
    { title: "Pain Point Matching", description: "Strategic mapping of their verified pain points to your achievements" },
    { title: "30-60-90 Day Quick Wins", description: "Three specific actions for your first 90 days" },
    { title: "The Gift", description: "A tangible, immediately usable tool they can use today" },
    { title: "\"Only I Can Do This\" Positioning", description: "Unique positioning based on your differentiators" },
    { title: "Confident Close", description: "Specific ask for 20 minutes with direct contact info" }
  ];

  return (
    <div className="min-h-screen bg-teal-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/jobintel-360-reports')} className="h-9 w-9 p-0 bg-white">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">JobIntel 360 Solution Asset</h1>
              <p className="text-muted-foreground mt-1">Instantly Become the #1 Candidate for the Job Everytime!</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/solution-asset-reports')} className="gap-2 bg-white">
            <FileText className="h-4 w-4" />
            My Reports
          </Button>
        </div>

        {/* Hero Card */}
        <Card className="border-2 border-teal-400 bg-teal-50 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Become the #1 Job Candidate Everytime!</h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                Why compete with <span className="font-bold">500+ applicants</span> when you can be the <span className="text-teal-600 font-bold">ONLY solution</span>?<br />
                The Solution Asset Strategy™ doesn't just get you noticed—it positions you as the executive-level<br />
                consultant they've been desperately searching for, before they even know they need you.
              </p>
            </div>

            {/* Two Info Links */}
            <div className="flex items-center justify-center gap-6 mt-4 mb-8">
              <Dialog open={showWhyDialog} onOpenChange={setShowWhyDialog}>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-amber-600 hover:text-amber-700 underline decoration-amber-400 underline-offset-4 font-semibold transition-colors">
                    <AlertTriangle className="w-4 h-4 mr-1.5" />
                    Why you need this
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="w-5 h-5" />
                      Why You Need This Strategy
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-6">
                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-teal-600">85%</div>
                          <div className="text-xs text-muted-foreground">Interview Success</div>
                        </div>
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-teal-600">72hrs</div>
                          <div className="text-xs text-muted-foreground">To Interview</div>
                        </div>
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-teal-600">95%</div>
                          <div className="text-xs text-muted-foreground">ATS Compatible</div>
                        </div>
                      </div>
                      
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">Why 99% of Applications Fail</h3>
                        <ul className="space-y-2 text-sm text-amber-900/80">
                          <li>• Generic cover letters that could apply to any company</li>
                          <li>• Hiring managers spend 7 seconds scanning applications</li>
                          <li>• "I'm excited to apply" is the kiss of death</li>
                          <li>• Competing against 500+ other generic applicants</li>
                        </ul>
                      </div>

                      <Separator />

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h3 className="font-semibold text-amber-800 mb-2">The $50,000 Consulting Firm Strategy</h3>
                        <p className="text-sm text-amber-900/80 mb-3">What McKinsey charges corporations $50k+ for... you get for FREE:</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <Search className="w-4 h-4 text-amber-600 mt-0.5" />
                            <span>Deep Intelligence: 600+ sources scanned</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5" />
                            <span>Research Mastery: 27 proven methodologies</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Award className="w-4 h-4 text-amber-600 mt-0.5" />
                            <span>Boardroom Presentation: Consulting-grade deliverables</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-amber-600 mt-0.5" />
                            <span>Executive Access: 87-step hunting process</span>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Before/After */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h4 className="font-bold text-red-800 mb-2 text-sm">❌ BEFORE: Traditional</h4>
                          <ul className="space-y-1 text-xs text-red-900/80">
                            <li>• Generic resume to 100+ companies</li>
                            <li>• 500+ applicant competition</li>
                            <li>• Weeks of silence</li>
                            <li>• 2-5% response rate</li>
                          </ul>
                        </div>
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                          <h4 className="font-bold text-teal-800 mb-2 text-sm">✅ AFTER: Solution Asset</h4>
                          <ul className="space-y-1 text-xs text-teal-900/80">
                            <li>• You become THE solution</li>
                            <li>• Zero competition</li>
                            <li>• 72hr interview calls</li>
                            <li>• 85% success rate</li>
                          </ul>
                        </div>
                      </div>

                      {/* Guarantee */}
                      <div className="bg-teal-50 border border-teal-300 rounded-lg p-4 text-center">
                        <Shield className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                        <h4 className="font-bold text-teal-800">85% Interview Success Guarantee</h4>
                        <p className="text-xs text-teal-700 mt-1">If you don't get interviews within 30 days, we work with you until you do.</p>
                      </div>
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              <span className="text-muted-foreground">|</span>

              <Dialog open={showWhatDialog} onOpenChange={setShowWhatDialog}>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center text-teal-600 hover:text-teal-700 underline decoration-teal-400 underline-offset-4 font-semibold transition-colors">
                    <Info className="w-4 h-4 mr-1.5" />
                    What you'll receive →
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-teal-600">
                      <Sparkles className="w-5 h-5" />
                      What You'll Receive
                    </DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="max-h-[65vh] pr-4">
                    <div className="space-y-4">
                      {whatYouReceiveItems.map((item, index) => (
                        <div key={index} className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-teal-800">{item.title}</p>
                              <p className="text-sm text-teal-700 mt-1">{item.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {/* Feature Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border-2 border-teal-600 rounded-xl p-5 text-center">
                <Target className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-foreground">85%</div>
                <div className="text-base font-semibold text-teal-600">Interview Success</div>
                <div className="text-sm text-muted-foreground">vs 2-5% average</div>
              </div>
              <div className="bg-white border-2 border-teal-600 rounded-xl p-5 text-center">
                <Clock className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-foreground">72hrs</div>
                <div className="text-base font-semibold text-teal-600">Apply to Interview</div>
                <div className="text-sm text-muted-foreground">vs 4-6 weeks wait</div>
              </div>
              <div className="bg-white border-2 border-teal-600 rounded-xl p-5 text-center">
                <Shield className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-foreground">95%</div>
                <div className="text-base font-semibold text-teal-600">ATS Compatibility</div>
                <div className="text-sm text-muted-foreground">Guaranteed bypass</div>
              </div>
              <div className="bg-white border-2 border-teal-600 rounded-xl p-5 text-center">
                <Gift className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-foreground">The Gift</div>
                <div className="text-base font-semibold text-teal-600">Tangible Value</div>
                <div className="text-sm text-muted-foreground">Immediate use</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Intelligence Source */}
        <Card className="border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
          <div className="bg-teal-50 border-b border-teal-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              Step 1: Select Your Intelligence Source
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Choose how you want to provide job intelligence</p>
          </div>
          <CardContent className="p-6 space-y-6">
            <Select value={sourceType || undefined} onValueChange={(value: SourceType) => setSourceType(value)}>
              <SelectTrigger className="w-full h-14 text-base border-2 border-dashed border-muted-foreground/30 hover:border-teal-500/50">
                <SelectValue placeholder="👆 Click here to select your intelligence source..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-gray-300 shadow-xl z-50">
                <SelectItem value="jobintel" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Use Existing JobIntel 360 Report</div><div className="text-xs text-muted-foreground">Select from your saved reports</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="company-insights" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Use Company Insights Report</div><div className="text-xs text-muted-foreground">Select from your company research</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="upload" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <Upload className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Upload Your Own Document</div><div className="text-xs text-muted-foreground">PDF, DOCX, or TXT file</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="paste" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <ClipboardPaste className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Copy & Paste Information</div><div className="text-xs text-muted-foreground">Paste job posting or research</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="generate-new" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <Plus className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Generate New JobIntel Report</div><div className="text-xs text-muted-foreground">Create fresh from job posting</div></div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Content based on selection */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
            ) : (
              <>
                {sourceType === 'jobintel' && (
                  jobIntelReports.length === 0 ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No JobIntel reports found.</p>
                      <Button onClick={() => navigate('/job-intel')}><Zap className="w-4 h-4 mr-2" />Create JobIntel Report First</Button>
                    </div>
                  ) : (
                    <div className="grid gap-3 max-h-60 overflow-y-auto">
                      {jobIntelReports.map((report) => (
                        <div key={report.id} onClick={() => setSelectedJobIntelId(report.id)} className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedJobIntelId === report.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' : 'border-border hover:border-teal-500/50'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-semibold">{report.job_title || 'Unknown Position'}</div>
                              <div className="text-sm text-muted-foreground">{report.company_name || 'Unknown Company'}</div>
                            </div>
                            {selectedJobIntelId === report.id && <CheckCircle className="w-5 h-5 text-teal-600" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {sourceType === 'company-insights' && (
                  companyInsightsReports.length === 0 ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No Company Insights reports found.</p>
                      <Button onClick={() => navigate('/company-insights')}><Building2 className="w-4 h-4 mr-2" />Create Report First</Button>
                    </div>
                  ) : (
                    <div className="grid gap-3 max-h-60 overflow-y-auto">
                      {companyInsightsReports.map((report) => (
                        <div key={report.id} onClick={() => setSelectedCompanyInsightId(report.id)} className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedCompanyInsightId === report.id ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20' : 'border-border hover:border-teal-500/50'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-semibold">{report.company_name || 'Unknown Company'}</div>
                              {report.job_title && <div className="text-sm text-muted-foreground">{report.job_title}</div>}
                            </div>
                            {selectedCompanyInsightId === report.id && <CheckCircle className="w-5 h-5 text-teal-600" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {sourceType === 'upload' && (
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                    <input type="file" accept=".pdf,.docx,.doc,.txt" onChange={(e) => setUploadedFile(e.target.files?.[0] || null)} className="hidden" id="file-upload" />
                    <label htmlFor="file-upload" className="cursor-pointer"><span className="text-teal-600 hover:underline">Click to upload</span> or drag and drop</label>
                    <p className="text-xs text-muted-foreground mt-2">PDF, DOCX, or TXT (max 10MB)</p>
                    {uploadedFile && <Badge variant="outline" className="mt-4 text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />{uploadedFile.name}</Badge>}
                  </div>
                )}
                {sourceType === 'paste' && (
                  <div className="space-y-4">
                    <Textarea value={pastedContent} onChange={(e) => setPastedContent(e.target.value)} placeholder="Paste job description, company research, or any intelligence..." rows={6} className="font-mono text-sm" />
                    {pastedContent.length > 0 && <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />{pastedContent.length} characters</Badge>}
                  </div>
                )}
                {sourceType === 'generate-new' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Job Posting URL (optional)</Label>
                      <div className="relative">
                        <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={newJobUrl} onChange={(e) => setNewJobUrl(e.target.value)} placeholder="https://linkedin.com/jobs/..." className="pl-10" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Job Description</Label>
                      <Textarea value={newJobDescription} onChange={(e) => setNewJobDescription(e.target.value)} placeholder="Paste the full job description here..." rows={5} />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Resume Selection */}
        <Card className="border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
          <div className="bg-teal-50 border-b border-teal-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Step 2: Your Resume
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Select how you'd like to provide your resume</p>
          </div>
          <CardContent className="p-6 space-y-6">
            <Select value={resumeSource || undefined} onValueChange={(value: 'library' | 'upload' | 'paste') => { setResumeSource(value); setSelectedResumeId(null); setSelectedResumeText(''); setResumeFile(null); setPastedResumeText(''); }}>
              <SelectTrigger className="w-full h-14 text-base border-2 border-dashed border-muted-foreground/30 hover:border-teal-500/50">
                <SelectValue placeholder="👆 Click here to choose your resume source..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-2 border-gray-300 shadow-xl z-50">
                <SelectItem value="library" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Use Resume from Library</div><div className="text-xs text-muted-foreground">Select from saved resumes</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="upload" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <Upload className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Upload New Resume</div><div className="text-xs text-muted-foreground">PDF or DOCX file</div></div>
                  </div>
                </SelectItem>
                <Separator className="my-1" />
                <SelectItem value="paste" className="py-3 my-1 rounded-md hover:bg-gray-100 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3">
                    <ClipboardPaste className="w-5 h-5 text-gray-600" />
                    <div><div className="font-semibold text-foreground">Paste Resume Text</div><div className="text-xs text-muted-foreground">Copy and paste content</div></div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {resumeSource === 'library' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                {loadingResumes ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /><span className="ml-2 text-blue-700">Loading resumes...</span></div>
                ) : userResumes.length === 0 ? (
                  <div className="text-center py-8"><AlertCircle className="w-12 h-12 text-blue-400 mx-auto mb-4" /><p className="text-blue-700 mb-4">No saved resumes found.</p><Button variant="outline" onClick={() => setResumeSource('upload')}><Upload className="w-4 h-4 mr-2" />Upload Instead</Button></div>
                ) : (
                  <Select value={selectedResumeId || undefined} onValueChange={(resumeId) => { const r = userResumes.find(r => r.id === resumeId); if (r) { setSelectedResumeId(resumeId); setSelectedResumeText(r.resume_text || ''); } }}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select a resume..." /></SelectTrigger>
                    <SelectContent className="bg-background">{userResumes.map((resume) => (<SelectItem key={resume.id} value={resume.id}>{resume.resume_name || resume.file_name || `Resume ${resume.id.slice(0, 8)}`}</SelectItem>))}</SelectContent>
                  </Select>
                )}
                {selectedResumeId && <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Resume selected</Badge>}
              </div>
            )}
            {resumeSource === 'upload' && (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                <input type="file" accept=".pdf,.docx,.doc" onChange={async (e) => { const file = e.target.files?.[0]; if (file) { setResumeFile(file); setProcessingResume(true); try { const { data } = await supabase.functions.invoke('convert-pdf-to-text', { body: { file_url: URL.createObjectURL(file) } }); setSelectedResumeText(data?.text || ''); } catch (err) { console.error(err); } finally { setProcessingResume(false); } } }} className="hidden" id="resume-upload" />
                <label htmlFor="resume-upload" className="cursor-pointer"><span className="text-teal-600 hover:underline">Click to upload</span></label>
                {resumeFile && <Badge variant="outline" className="mt-4 text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />{resumeFile.name}</Badge>}
              </div>
            )}
            {resumeSource === 'paste' && (
              <div className="space-y-4">
                <Textarea value={pastedResumeText} onChange={(e) => setPastedResumeText(e.target.value)} placeholder="Paste your resume content here..." rows={6} className="font-mono text-sm" />
                {pastedResumeText.length > 0 && <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />{pastedResumeText.length} characters</Badge>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Additional Context */}
        <Card className="border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
          <div className="bg-teal-50 border-b border-teal-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-teal-600" />
              Step 3: Additional Context (Optional)
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Add supporting documents, notes, or research URLs</p>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Upload */}
            <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center hover:border-teal-500/50 transition-colors">
              <input type="file" id="additional-files" accept=".pdf,.doc,.docx,.txt" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); setAdditionalFiles(prev => [...prev, ...files.map(f => ({ file: f, name: f.name, processing: false }))]); }} />
              <label htmlFor="additional-files" className="cursor-pointer"><Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><span className="text-teal-600 font-medium hover:underline">Upload supporting docs</span></label>
            </div>
            {additionalFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">{additionalFiles.map((file, i) => (<Badge key={i} variant="outline" className="bg-green-50 border-green-300"><CheckCircle className="w-3 h-3 mr-1 text-green-600" />{file.name}<button onClick={() => setAdditionalFiles(prev => prev.filter((_, idx) => idx !== i))} className="ml-2 hover:text-destructive"><X className="w-3 h-3" /></button></Badge>))}</div>
            )}

            {/* Paste */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><ClipboardPaste className="w-4 h-4" />Paste Additional Notes</Label>
              <Textarea value={additionalPastedContent} onChange={(e) => setAdditionalPastedContent(e.target.value)} placeholder="Cover letter drafts, notes, achievements..." rows={4} className="font-mono text-sm" />
            </div>

            {/* URLs */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Globe className="w-4 h-4" />Research URLs</Label>
              <div className="flex gap-2">
                <Input value={newResearchUrl} onChange={(e) => setNewResearchUrl(e.target.value)} placeholder="https://company.com/about" className="flex-1" />
                <Button type="button" variant="outline" onClick={() => { if (newResearchUrl.trim()) { setResearchUrls(prev => [...prev, { url: newResearchUrl.trim(), status: 'pending' }]); setNewResearchUrl(''); } }}><Plus className="w-4 h-4" /></Button>
              </div>
              {researchUrls.length > 0 && (
                <div className="space-y-2 mt-2">{researchUrls.map((urlData, i) => (<div key={i} className="flex items-center gap-2 p-2 bg-muted/50 rounded-md"><Globe className="w-4 h-4 text-muted-foreground" /><span className="flex-1 text-sm truncate">{urlData.url}</span><button onClick={() => setResearchUrls(prev => prev.filter((_, idx) => idx !== i))} className="hover:text-destructive"><X className="w-4 h-4" /></button></div>))}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Generate Button */}
        <div className="flex justify-between gap-4">
          <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-teal-300 hover:bg-teal-50"><Eye className="w-4 h-4 mr-2" />View Sample Report</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
              <DialogHeader className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border-b">
                <DialogTitle className="flex items-center text-xl font-bold"><Star className="w-6 h-6 mr-2 text-teal-600" />Sample Solution Asset<Badge className="ml-auto bg-teal-600 text-white">SAMPLE</Badge></DialogTitle>
              </DialogHeader>
              <ScrollArea className="flex-1">
                <div className="p-6 space-y-6">
                  <Card className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 p-6">
                    <h2 className="text-2xl font-bold">{solutionAssetData.candidate.name}</h2>
                    <p className="text-teal-600 font-semibold">{solutionAssetData.candidate.targetRole}</p>
                  </Card>
                  <Card><CardHeader className="bg-teal-50"><CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-teal-600" />Headline Outcome</CardTitle></CardHeader><CardContent className="p-4"><p className="font-semibold">{solutionAssetData.headline}</p></CardContent></Card>
                  <Card><CardHeader className="bg-red-50"><CardTitle className="flex items-center gap-2"><TrendingDown className="w-5 h-5 text-red-600" />Pain Points</CardTitle></CardHeader><CardContent className="p-4"><div className="space-y-2">{solutionAssetData.painPoints.map((p, i) => (<div key={i} className="p-3 bg-red-50/50 rounded border border-red-200"><div className="font-semibold text-red-700">{p.title}</div><p className="text-sm text-muted-foreground">{p.description}</p></div>))}</div></CardContent></Card>
                  <Card><CardHeader className="bg-teal-50"><CardTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-teal-600" />Solutions</CardTitle></CardHeader><CardContent className="p-4"><div className="space-y-3">{solutionAssetData.solutions.map((s, i) => (<div key={i} className="p-3 bg-teal-50/50 rounded border border-teal-200"><div className="font-semibold text-teal-700">{s.title}</div><p className="text-sm"><strong>Action:</strong> {s.action}</p><p className="text-sm text-teal-600"><strong>Impact:</strong> {s.impact}</p></div>))}</div></CardContent></Card>
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          <Button size="lg" onClick={handleGenerate} disabled={isGenerating || !isSourceValid() || !isResumeValid()} className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-8">
            {isGenerating ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</>) : (<><Rocket className="w-5 h-5 mr-2" />Generate Solution Asset</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
